import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { deleteDTRAttachment } from "services/learnPointUserServices";

// Discrete file attachments stage locally and upload only on the parent's Save action
// (mirrors the deferred-upload design in RichTextEditorField.js). existingAttachments is
// owned/fetched by the parent (ID-based getDTRAttachmentsByParentId), passed down as a prop.
// Removing an EXISTING attachment now requires confirmation (see confirmDeleteTarget below).

const MAX_ATTACHMENT_SIZE_BYTES = 3 * 1024 * 1024; // 3MB — server-side is authoritative;
// this is a UX-only pre-check to skip a pointless round trip.

const MAX_FILE_ATTACHMENTS = 2; // also enforced server-side.

const ALLOWED_EXTENSIONS = [
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".pdf",
  ".eml",
  ".msg",
  ".txt",
  // Kept in sync with the server-side allowlist (TrackerLearnPointService.cs).
  ".jpg",
  ".jpeg",
  ".png",
];

const getExtension = (fileName) => {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : "";
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DTRAttachmentList({
  existingAttachments = [],
  isReadOnly = false,
  onPendingFilesChange = undefined,
  onExistingRemoved = undefined,
  resetSignal = 0,
}) {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [error, setError] = useState("");
  // Enhancement: confirm before deleting an already-uploaded attachment (holds the
  // attachment awaiting confirmation, or null when the dialog is closed).
  const [confirmDeleteTarget, setConfirmDeleteTarget] = useState(null);

  // Notify the parent on every change to the staged list, so it can include these files in
  // the combined-save FormData and keep the attachment-summary bar accurate in real time.
  useEffect(() => {
    onPendingFilesChange?.(pendingFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFiles]);

  // Resets staged files when the parent signals a new context (date/report-type change, or a
  // successful save) via resetSignal, an explicitly-bumped counter — NOT derived from
  // existingAttachments, which is a fresh array reference every render.
  useEffect(() => {
    setPendingFiles([]);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const totalCount = existingAttachments.length + pendingFiles.length;
  const atMaxAttachments = totalCount >= MAX_FILE_ATTACHMENTS;

  const onDrop = useCallback(
    (acceptedFiles) => {
      setError("");
      const accepted = [];

      for (const file of acceptedFiles) {
        if (
          existingAttachments.length + pendingFiles.length + accepted.length >=
          MAX_FILE_ATTACHMENTS
        ) {
          setError(
            `This entry already has the maximum of ${MAX_FILE_ATTACHMENTS} file attachments.`,
          );
          break;
        }

        const extension = getExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          setError(
            `"${file.name}" isn't an allowed file type (Word, Excel, PDF, email, text, or JPG/PNG only).`,
          );
          continue;
        }
        if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
          setError(`"${file.name}" exceeds the 3MB attachment limit.`);
          continue;
        }

        const alreadyThere =
          existingAttachments.some((a) => a.FileName === file.name) ||
          pendingFiles.some((p) => p.file.name === file.name) ||
          accepted.some((p) => p.file.name === file.name);
        if (alreadyThere) {
          setError(`"${file.name}" is already attached.`);
          continue;
        }

        accepted.push({ file, kind: "FileAttachment" });
      }

      if (accepted.length > 0) {
        setPendingFiles((prev) => [...prev, ...accepted]);
      }
    },
    [existingAttachments, pendingFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isReadOnly || atMaxAttachments,
    multiple: true,
    // Constrains the browser's own file picker to these extensions too.
    accept: {
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/pdf": [".pdf"],
      "message/rfc822": [".eml"],
      "application/vnd.ms-outlook": [".msg"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
  });

  const handleRemovePending = (file) => {
    setPendingFiles((prev) => prev.filter((p) => p.file !== file));
  };

  const handleRemoveExisting = async (attachment) => {
    setError("");
    try {
      const response = await deleteDTRAttachment(
        attachment.LP_DTRAttachmentId,
        attachment.FileDocLibId,
      );
      if (response?.statusCode === 200) {
        onExistingRemoved?.(attachment.LP_DTRAttachmentId);
      } else {
        setError(response?.message || "Failed to remove attachment.");
      }
    } catch {
      setError("Failed to remove attachment.");
    }
  };

  const handleConfirmRemoveExisting = async () => {
    const attachment = confirmDeleteTarget;
    setConfirmDeleteTarget(null);
    if (attachment) await handleRemoveExisting(attachment);
  };

  // ASTT-7319: extracted into a variable rather than moved in place --
  // the content itself is unchanged, just referenced at a new position
  // below (after the drop-zone instead of before it), per direct
  // request. Extracting avoids physically relocating ~80 lines of JSX.
  const attachmentChips = (existingAttachments.length > 0 || pendingFiles.length > 0) && (
        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {existingAttachments.map((attachment) => (
            <Chip
              key={attachment.LP_DTRAttachmentId}
              icon={<InsertDriveFileIcon />}
              label={
                <Tooltip title={attachment.FileName} arrow>
                  <Link
                    href={attachment.FileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: "inherit" }}
                  >
                    {attachment.FileName}
                    {attachment.FileSizeBytes
                      ? ` (${formatFileSize(attachment.FileSizeBytes)})`
                      : ""}
                  </Link>
                </Tooltip>
              }
              onDelete={
                isReadOnly ? undefined : () => setConfirmDeleteTarget(attachment)
              }
              deleteIcon={isReadOnly ? undefined : <DeleteOutlineIcon />}
              sx={{
                maxWidth: 280,
                backgroundColor: "#fff",
                border: "1px solid #d3d3d3",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          ))}

          {pendingFiles.map((pending, idx) => (
            <Chip
              // eslint-disable-next-line react/no-array-index-key
              key={`pending-${idx}-${pending.file.name}`}
              icon={<HourglassEmptyIcon />}
              label={
                <Tooltip
                  title="Uploaded when you click Save"
                  arrow
                >
                  <span>
                    {pending.file.name}
                    {pending.file.size
                      ? ` (${formatFileSize(pending.file.size)})`
                      : ""}{" "}
                    — pending
                  </span>
                </Tooltip>
              }
              onDelete={
                isReadOnly
                  ? undefined
                  : () => handleRemovePending(pending.file)
              }
              deleteIcon={isReadOnly ? undefined : <DeleteOutlineIcon />}
              sx={{
                maxWidth: 300,
                backgroundColor: "#FFF8E1",
                border: "1px dashed #C9A227",
                fontStyle: "italic",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          ))}
        </Box>
      );

  return (
    <Box>
      {!isReadOnly && (
        <Box
          {...getRootProps()}
          sx={{
            border: "1px dashed #b0bec5",
            borderRadius: "8px",
            padding: "10px 14px",
            textAlign: "center",
            cursor: atMaxAttachments ? "default" : "pointer",
            backgroundColor: isDragActive ? "#E0F2FF" : "#fafafa",
            color: "#607d8b",
            fontSize: "0.8rem",
            opacity: atMaxAttachments ? 0.6 : 1,
            // ASTT-7319 follow-up: this control now shares a half-width
            // column with Email Distro (was a full-width row by itself),
            // so a small fixed 260px width now looks cramped rather than
            // appropriately sized -- filling the column instead, and
            // increasing height further to visually match the substantial
            // size of the rich-text editor fields (Topics Covered/
            // Parking Lot) it's now paired alongside in the same row
            // pattern.
            width: "100%",
            minHeight: 50,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input {...getInputProps()} />
          {atMaxAttachments ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <span>
                Maximum of {MAX_FILE_ATTACHMENTS} file attachments reached for
                this entry.
              </span>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <AttachFileIcon fontSize="small" />
              <span>
                {isDragActive
                  ? "Drop file to attach"
                  : "Drag a file here, or click to browse (Word, Excel, PDF, email, text, JPG/PNG — max 3MB, up to 2 files — uploaded when you Save)"}
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* ASTT-7319: attached files list now renders below the drop-zone
          control, was above it. */}
      {attachmentChips}

      {error && (
        <Typography
          variant="body2"
          sx={{ color: "red", mt: 0.5, fontSize: "0.75rem" }}
        >
          {error}
        </Typography>
      )}

      {/* Enhancement: confirm before deleting an already-uploaded attachment. */}
      <Dialog
        open={Boolean(confirmDeleteTarget)}
        onClose={() => setConfirmDeleteTarget(null)}
      >
        <DialogTitle>Remove attachment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`"${confirmDeleteTarget?.FileName || ""}" will be permanently removed from this entry. This can't be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleConfirmRemoveExisting} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

DTRAttachmentList.propTypes = {
  // Already-saved FileAttachment-kind rows for this entry, owned/fetched by the parent via
  // getDTRAttachmentsByParentId (empty array for a brand-new, never-saved entry).
  existingAttachments: PropTypes.arrayOf(
    PropTypes.shape({
      LP_DTRAttachmentId: PropTypes.number,
      FileName: PropTypes.string,
      FileUrl: PropTypes.string,
      FileDocLibId: PropTypes.number,
      FileSizeBytes: PropTypes.number,
    }),
  ),
  isReadOnly: PropTypes.bool,
  // Fired on every change to the staged file list, for the parent's combined-save request.
  onPendingFilesChange: PropTypes.func,
  // Fired after an existing attachment is deleted (attachmentId), to update parent state.
  onExistingRemoved: PropTypes.func,
  // Bump to signal staged files should clear (date/report-type change, or after a save).
  resetSignal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
